import { Classes } from "@blueprintjs/core";
import { HexType, guardNever } from "@hex/common";
import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { SetRequired } from "type-fest";

import { HexTooltip, Txt } from "../../../../hex-components";
import {
  HexPopoverProps,
  StyleableHexPopover,
} from "../../../../hex-components/HexPopover";
import { ProjectsTableRowData } from "../../../projects-table/types";

import { DynamicFlex, DynamicFlexProps } from "./OverflowAwareFlex";
import { getUsageCellLabel } from "./getUsageCellLabel";
import { UsageCellLink } from "./UsagesCellLink";
import { UsagesPopoverContent } from "./UsagesPopoverContent";

export const UsagesTableCellRenderer = React.memo<{
  data: Pick<ProjectsTableRowData, "cells" | "hexType">;
}>(function UsagesTableCellRenderer({ data }) {
  if (
    data.cells == null ||
    data.hexType === HexType.EXPLORE ||
    data.hexType === HexType.ASK
  ) {
    // "Ask" projects are not user-visible
    // "Explore" projects are single cell, so "Usages" is not relevant
    return null;
  } else if (
    data.hexType === HexType.PROJECT ||
    data.hexType === HexType.COMPONENT
  ) {
    return <UsagesTableCellRenderer_ data={{ cells: data.cells }} />;
  } else {
    guardNever(data.hexType, data.hexType);
    return null;
  }
});

/**
 * The component should
 * - render as many cell labels as possible within the available width.
 * - display a count of how many labels are not visible
 * - render the last label as ellipsized if necessary (not hiding it)
 * - render a popover when there are labels not visible
 *
 * Example:
 *
 * ... | Usages | ...
 * --- | ------ | ---
 * ... | Label 1, Label 2, La..., +3 | ... (with popover)
 * ... | Label 1, AbcdefgLabel 2, +4 | ... (with popover)
 * ... | Label 1, Label 2            | ... (no popover)
 * ... | Label 1, Looooooooong La... | ... (no popover)
 * ... | Label 1, Looooong La..., +1 | ... (with popover)
 */
const UsagesTableCellRenderer_ = React.memo<{
  data: SetRequired<Pick<ProjectsTableRowData, "cells">, "cells">;
}>(function UsagesTableCellRenderer_({ data }) {
  const count = data.cells.length;
  const [overflowing, setOverflowing] = useState(false);

  const renderTarget = useCallback<
    Exclude<HexPopoverProps["renderTarget"], undefined>
  >(
    ({ isOpen: _isPopoverOpen, ...popoverTargetProps }) => {
      return (
        <DynamicFlex
          count={count}
          partialMinWidth={52} // avoid rendering an purely ellipsized Txt like "...". should be at least one char
          renderEndDecorator={renderEndDecorator}
          strategy="partial"
          onOverflowingChange={setOverflowing}
          {...popoverTargetProps}
        >
          {(state: { overflowCount: number }) =>
            data.cells.map((cell, index) => (
              <Txt
                key={cell.id}
                ellipsize={count - state.overflowCount === index + 1}
                fontColor="muted"
                fontSize="small"
                title={undefined}
              >
                {index > 0 && ", "}
                {cell.onlyExistsInPublished ? (
                  <HexTooltip
                    alignContent="left"
                    content="Only exists in published version"
                    position="bottom-left"
                    // eslint-disable-next-line react/jsx-no-bind -- fine
                    renderTarget={({
                      isOpen: _isTooltipOpen,
                      ...tooltipTargetProps
                    }) => (
                      <UsageCellLink cell={cell} {...tooltipTargetProps}>
                        {getUsageCellLabel({ cell })}
                      </UsageCellLink>
                    )}
                  />
                ) : (
                  <UsageCellLink cell={cell}>
                    {getUsageCellLabel({ cell })}
                  </UsageCellLink>
                )}
              </Txt>
            ))
          }
        </DynamicFlex>
      );
    },
    [count, data.cells],
  );

  return (
    <StyledHexPopover
      content={<UsagesPopoverContent cells={data.cells} />}
      disabled={!overflowing}
      interactionKind="hover"
      placement="right-start"
      renderTarget={renderTarget}
    />
  );
});

const renderEndDecorator: DynamicFlexProps["renderEndDecorator"] = (
  props,
  { count, overflowCount },
) => {
  if (overflowCount === 0) {
    return null;
  } else if (count === overflowCount) {
    return (
      <Txt ellipsize={true} fontColor="muted" fontSize="small" {...props}>
        {count} cells
      </Txt>
    );
  } else {
    return (
      <Txt fontColor="muted" fontSize="small" {...props}>
        , +{overflowCount}
      </Txt>
    );
  }
};

const StyledHexPopover = styled(StyleableHexPopover)`
  .${Classes.POPOVER_CONTENT} {
    background: ${({ theme }) => theme.backgroundColor.MUTED};
    max-width: 306px;
    padding: 12px;
  }
  .${Classes.POPOVER_ARROW} {
    background: ${({ theme }) => theme.backgroundColor.MUTED};
  }
`;
